import type { Pence } from "./types.ts";

export type WeightUnit = "kg" | "g" | "lb" | "oz";
export type LengthUnit = "cm" | "mm" | "in";

const KG_PER: Record<WeightUnit, number> = {
  kg: 1,
  g: 0.001,
  lb: 0.45359237,
  oz: 0.028349523125,
};

const CM_PER: Record<LengthUnit, number> = {
  cm: 1,
  mm: 0.1,
  in: 2.54,
};

/** Round to a fixed number of decimal places without binary drift. */
export function round(value: number, places: number): number {
  const f = 10 ** places;
  return Math.round((value + Number.EPSILON) * f) / f;
}

export function toKg(value: number, unit: WeightUnit): number {
  return round(value * KG_PER[unit], 3);
}

export function toCm(value: number, unit: LengthUnit): number {
  return round(value * CM_PER[unit], 2);
}

export function gbpToPence(gbp: number): Pence {
  return Math.round((gbp + Number.EPSILON) * 100);
}

export function formatGbp(pence: Pence): string {
  const sign = pence < 0 ? "-" : "";
  const abs = Math.abs(pence);
  const pounds = Math.floor(abs / 100).toLocaleString("en-GB");
  const p = String(abs % 100).padStart(2, "0");
  return `${sign}£${pounds}.${p}`;
}

/** Parse a number from a spreadsheet cell: tolerates "£1,234.50", " 2.5 ", "1.2kg". */
export function parseNumber(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== "string") return null;
  const cleaned = raw.trim().replace(/[£,\s]/g, "").replace(/[a-z]+$/i, "");
  if (cleaned === "" || !/^-?\d*\.?\d+$/.test(cleaned)) return null;
  return Number(cleaned);
}
